import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Link
} from '@mui/material';
import { Close as CloseIcon, Handshake as PartnerIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTermsContent } from '../hooks/useTermsContent';
import { introductionFeeCopyVars, introductionFeeLadder } from '../config/plans';
import { TERMS_VERSION } from '../config/terms';
import { planNameKey } from '../utils/partnerPlan';

const PartnerTermsModal = ({ open, onClose, onAccept }) => {
  const { t } = useTranslation();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedFees, setAcceptedFees] = useState(false);
  const [acceptedFeeChanges, setAcceptedFeeChanges] = useState(false);
  
  const { content, loading, error } = useTermsContent('partner');
  const feeLadder = introductionFeeLadder((id) => t(planNameKey(id)));
  const feeCopy = introductionFeeCopyVars();

  const handleAccept = () => {
    if (acceptedTerms && acceptedFees && acceptedFeeChanges) {
      onAccept();
    }
  };

  const allAccepted = acceptedTerms && acceptedFees && acceptedFeeChanges;
  const termsVersion = TERMS_VERSION;

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        'data-testid': 'partner-terms-modal-paper',
        sx: {
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'min(90dvh, 90vh)',
          width: '100%',
          m: { xs: 1, sm: 2 },
          borderRadius: 2,
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexShrink: 0,
        pb: 1,
        bgcolor: 'primary.main',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PartnerIcon />
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
            Partner Agreement
          </Typography>
        </Box>
        <Button
          onClick={handleAccept}
          aria-label={t('common.close')}
          data-testid="partner-terms-close-button"
          sx={{ minWidth: 'auto', p: 1, color: 'white' }}
          disabled={!allAccepted}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>
      
      <DialogContent
        dividers
        data-testid="partner-terms-modal-content"
        sx={{ 
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          p: 3,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '4px',
          },
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="error" paragraph>
              Failed to load partner agreement. Please try again later.
            </Typography>
            <Link href="/terms-partner.html" target="_blank" rel="noopener">
              View Partner Agreement in new tab
            </Link>
          </Box>
        ) : (
          <Box 
            sx={{ 
              '& h2': { 
                color: '#1a365d', 
                fontSize: '1.4rem',
                fontWeight: 600,
                mt: 3,
                mb: 2,
                pb: 1,
                borderBottom: '2px solid #f0f0f0'
              },
              '& h3': {
                fontSize: '1rem',
                fontWeight: 600,
                mt: 2,
                mb: 1
              },
              '& p': {
                mb: 2,
                lineHeight: 1.6,
                fontSize: '0.9rem'
              },
              '& ul': {
                pl: 3,
                mb: 2
              },
              '& li': {
                mb: 0.5,
                fontSize: '0.9rem'
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                mb: 2,
                fontSize: '0.85rem'
              },
              '& th': {
                bgcolor: '#1a365d',
                color: 'white',
                p: 1,
                textAlign: 'left'
              },
              '& td': {
                p: 1,
                borderBottom: '1px solid #ddd'
              },
              '& tr:nth-of-type(even)': {
                bgcolor: '#f8f9fa'
              },
              '& .highlight-box': {
                bgcolor: '#f0f7ff',
                borderLeft: '4px solid #1a365d',
                p: 2,
                my: 2,
                borderRadius: '0 8px 8px 0'
              },
              '& .warning-box': {
                bgcolor: '#fff3cd',
                borderLeft: '4px solid #ffc107',
                p: 2,
                my: 2,
                borderRadius: '0 8px 8px 0'
              },
              '& .contact-box': {
                bgcolor: '#e8f4f8',
                p: 2,
                borderRadius: 2,
                mt: 2
              },
              '& .last-updated': {
                color: '#666',
                fontSize: '0.85rem',
                mb: 2,
                pb: 1,
                borderBottom: '1px solid #eee'
              },
              '& a': {
                color: '#1a365d'
              }
            }}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )}

        <Box sx={{
          mt: 3,
          bgcolor: 'grey.50',
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.300',
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Partner Acceptance (All Required)
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                color="primary"
                inputProps={{ 'data-testid': 'partner-terms-terms-checkbox' }}
              />
            }
            label={
              <Typography variant="body2">
                <strong>1.</strong> I have read and agree to the Partner Agreement terms and conditions, 
                including my obligations as a partner and the platform's liability limitations.
              </Typography>
            }
            sx={{ mb: 2, alignItems: 'flex-start' }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedFees}
                onChange={(e) => setAcceptedFees(e.target.checked)}
                color="primary"
                inputProps={{ 'data-testid': 'partner-terms-fees-checkbox' }}
              />
            }
            label={
              <Typography variant="body2">
                <strong>2.</strong>{' '}
                {t('partnerTerms.fees', { feeLadder, ...feeCopy })}
              </Typography>
            }
            sx={{ mb: 2, alignItems: 'flex-start' }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedFeeChanges}
                onChange={(e) => setAcceptedFeeChanges(e.target.checked)}
                color="primary"
                inputProps={{ 'data-testid': 'partner-terms-fee-changes-checkbox' }}
              />
            }
            label={
              <Typography variant="body2">
                <strong>3.</strong> I acknowledge that Déjà Vu Nomad may <strong>modify commission rates 
                and introduce new fees</strong> in the future with 30 days notice, and that my continued 
                use of the platform after such changes constitutes acceptance.
              </Typography>
            }
            sx={{ alignItems: 'flex-start' }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ flexShrink: 0, p: 2, pt: 1, bgcolor: 'grey.100' }}>
        <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary' }}>
          Version {termsVersion} | All checkboxes required
        </Typography>
        <Button
          onClick={handleAccept}
          variant="contained"
          disabled={!allAccepted}
          data-testid="partner-terms-accept-button"
          sx={{ 
            minWidth: 180,
            fontWeight: 600
          }}
        >
          Accept & Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartnerTermsModal;
